import {
  createContext,
  useContext,
  useLayoutEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { useI18nContext } from "@hopper-b2b/i18n";
import { BackButton, MobileFloatingSection } from "@hopper-b2b/ui";
import clsx from "clsx";
import TrashIcon from "../../../assets/client/trash.svg?react";
import { NubankSimpleModal } from "../../NubankSimpleModal";
import "./styles.scss";

// ── Header actions context ──
// Lets the form buttons (deep in the children) register their handlers
// so the modal header can render save/delete buttons.

interface HeaderActions {
  onSave?: () => void;
  onDelete?: (id: string) => void;
  travelerId?: string;
  disabled?: boolean;
}

const HeaderActionsContext = createContext<{
  setActions: (actions: HeaderActions) => void;
  active: boolean;
}>({ setActions: () => {}, active: false });

const useHeaderActions = () => useContext(HeaderActionsContext);

/**
 * Slot component for "mobile-traveler-edit-form-buttons".
 * Registers save/delete handlers in the header context so NubankSimpleModal
 * can render them in the header. Also renders fallback bottom buttons
 * (hidden via CSS).
 */
export const HeaderFormButtons = ({
  onSave,
  onDelete,
  travelerId,
  disabled,
}: {
  onSave?: () => void;
  onDelete?: (id: string) => void;
  travelerId?: string;
  disabled?: boolean;
}) => {
  const { setActions, active } = useHeaderActions();
  const { t } = useI18nContext();

  useLayoutEffect(() => {
    if (active) {
      setActions({ onSave, onDelete, travelerId, disabled });
    }
  }, [active, onSave, onDelete, travelerId, disabled, setActions]);

  // Bottom buttons hidden via CSS — header buttons are the visible ones
  return (
    <div
      className={clsx(
        "floating-box traveler-info-form-docked-buttons-container",
        { hidden: disabled }
      )}
    >
      <MobileFloatingSection
        className="traveler-info-form-button-container"
        primaryButton={{
          children: travelerId ? t("saveChanges") : t("savePassenger"),
          onClick: onSave,
          className: "mobile-traveler-info-form-save-button",
          disabled,
        }}
        secondaryButton={
          travelerId && onDelete
            ? {
                children: t("deletePassenger"),
                onClick: () => onDelete(travelerId),
                className: "traveler-info-form-delete-button",
                wrapperClassName: "traveler-info-form-section delete",
              }
            : null
        }
      />
    </div>
  );
};

// ── Header buttons ──

const HeaderSaveButton = ({ actions }: { actions: HeaderActions }) => {
  const { t } = useI18nContext();
  return (
    <button
      className="traveller-form-header-save"
      onClick={actions.onSave}
      disabled={actions.disabled}
    >
      {t("travelerFormSaveButton")}
    </button>
  );
};

const HeaderDeleteButton = ({ actions }: { actions: HeaderActions }) => {
  if (!actions.travelerId || !actions.onDelete) return null;
  return (
    <button
      className="traveller-form-header-delete"
      onClick={() => actions.onDelete?.(actions.travelerId as string)}
    >
      <TrashIcon width={20} height={20} />
    </button>
  );
};

// ── Modal ──

interface TravellerFormModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}

export const TravellerFormModal = ({
  open,
  onClose,
  children,
  title,
  className,
}: TravellerFormModalProps) => {
  const [actions, setActions] = useState<HeaderActions>({});

  const handleSetActions = useCallback((a: HeaderActions) => {
    setActions((prev) => {
      if (
        prev.onSave !== a.onSave ||
        prev.onDelete !== a.onDelete ||
        prev.travelerId !== a.travelerId ||
        prev.disabled !== a.disabled
      ) {
        return a;
      }
      return prev;
    });
  }, []);

  return (
    <HeaderActionsContext.Provider
      value={{ setActions: handleSetActions, active: true }}
    >
      <NubankSimpleModal
        open={open}
        onClose={onClose}
        className={clsx(
          "nubank-traveller-form-modal",
          "traveler-select-workflow-info-form-popup",
          "mobile",
          className
        )}
        topLeftButton={
          <BackButton
            className="traveler-entries-select-popup-back-button"
            onClick={onClose}
          />
        }
        topRightButton={
          actions.onSave ? <HeaderSaveButton actions={actions} /> : undefined
        }
        headerElement={
          title ? (
            <h4 className="traveler-select-workflow-info-form-header">
              {title}
              <HeaderDeleteButton actions={actions} />
            </h4>
          ) : null
        }
      >
        {children}
      </NubankSimpleModal>
    </HeaderActionsContext.Provider>
  );
};
